import { z } from 'zod';

import {
  dayOfWeekValues,
  type CreateEmployeeScheduleRequest,
  type CreateEmployeeScheduleResponse,
  type DayOfWeek,
  type DeleteEmployeeScheduleRequest,
  type DeleteEmployeeScheduleResponse,
  type EmployeeScheduleRuleDto,
  type ListEmployeeSchedulesRequest,
  type ListEmployeeSchedulesResponse,
  type ListMySchedulesRequest,
  type ListMySchedulesResponse,
  type UpdateEmployeeScheduleRequest,
  type UpdateEmployeeScheduleResponse,
} from './employee-schedules.dto.js';

const timestampSchema = z.iso.datetime();
const dateSchema = z.iso.date();
const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
  message: 'Invalid time format (HH:mm)',
});

export const DayOfWeekSchema: z.ZodType<DayOfWeek> = z.union([
  z.literal(dayOfWeekValues[0]),
  z.literal(dayOfWeekValues[1]),
  z.literal(dayOfWeekValues[2]),
  z.literal(dayOfWeekValues[3]),
  z.literal(dayOfWeekValues[4]),
  z.literal(dayOfWeekValues[5]),
  z.literal(dayOfWeekValues[6]),
]);

export const EmployeeScheduleRuleDtoSchema: z.ZodType<EmployeeScheduleRuleDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  employeeId: z.uuid(),
  effectiveFrom: dateSchema,
  effectiveTo: dateSchema.nullable(),
  dayOfWeek: DayOfWeekSchema,
  startLocalTime: localTimeSchema,
  endLocalTime: localTimeSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const ListEmployeeSchedulesRequestSchema: z.ZodType<ListEmployeeSchedulesRequest> = z.object({
  companyId: z.uuid(),
  employeeId: z.uuid(),
});

export const ListEmployeeSchedulesResponseSchema: z.ZodType<ListEmployeeSchedulesResponse> = z.object({
  items: z.array(EmployeeScheduleRuleDtoSchema),
});

export const ListMySchedulesRequestSchema: z.ZodType<ListMySchedulesRequest> = z.object({
  companyId: z.uuid(),
});

export const ListMySchedulesResponseSchema: z.ZodType<ListMySchedulesResponse> = z.object({
  items: z.array(EmployeeScheduleRuleDtoSchema),
});

export const CreateEmployeeScheduleRequestSchema: z.ZodType<CreateEmployeeScheduleRequest> = z.object({
  companyId: z.uuid(),
  employeeId: z.uuid(),
  effectiveFrom: dateSchema,
  effectiveTo: dateSchema.nullable().optional(),
  dayOfWeek: DayOfWeekSchema,
  startLocalTime: localTimeSchema,
  endLocalTime: localTimeSchema,
});

export const CreateEmployeeScheduleResponseSchema: z.ZodType<CreateEmployeeScheduleResponse> = z.object({
  schedule: EmployeeScheduleRuleDtoSchema,
});

export const UpdateEmployeeScheduleRequestSchema: z.ZodType<UpdateEmployeeScheduleRequest> = z.object({
  companyId: z.uuid(),
  employeeId: z.uuid(),
  scheduleId: z.uuid(),
  effectiveFrom: dateSchema.optional(),
  effectiveTo: dateSchema.nullable().optional(),
  dayOfWeek: DayOfWeekSchema.optional(),
  startLocalTime: localTimeSchema.optional(),
  endLocalTime: localTimeSchema.optional(),
});

export const UpdateEmployeeScheduleResponseSchema: z.ZodType<UpdateEmployeeScheduleResponse> = z.object({
  schedule: EmployeeScheduleRuleDtoSchema,
});

export const DeleteEmployeeScheduleRequestSchema: z.ZodType<DeleteEmployeeScheduleRequest> = z.object({
  companyId: z.uuid(),
  employeeId: z.uuid(),
  scheduleId: z.uuid(),
});

export const DeleteEmployeeScheduleResponseSchema: z.ZodType<DeleteEmployeeScheduleResponse> = z.object({
  scheduleId: z.uuid(),
});
